import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * A text box whose value is kept in the URL (`?q=`), so a record page's back
 * arrow returns to the list with the search as it was left.
 *
 * THE BOX CANNOT READ ITS VALUE STRAIGHT FROM THE URL. A navigation lands a
 * render later than the keystroke, so a controlled input fed from
 * `searchParams` is reset to the old value between keys, and typing "jim"
 * quickly leaves "m" in the box. jsdom's router is synchronous enough that no
 * test sees it; a browser does (found on /payroll/approvals, 2026-10-20).
 *
 * So the box holds its own text and writes every change through to the URL.
 * The URL's value flows back in only when it changes for some OTHER reason —
 * Back, a link, a tab change that clears it. Our own writes arrive back as
 * echoes, possibly several behind the box, and are recognised and ignored:
 * re-adopting an echo of "j" while the box already reads "ji" is the bug again
 * in a different place.
 */
export function useUrlBackedText(urlValue: string, write: (next: string) => void): [string, (next: string) => void] {
  const [text, setText] = useState(urlValue);
  const pending = useRef<string[]>([]);

  useEffect(() => {
    const i = pending.current.indexOf(urlValue);
    if (i >= 0) {
      // An echo of a write of ours: drop it and everything written before it.
      pending.current = pending.current.slice(i + 1);
      return;
    }
    pending.current = [];
    setText(urlValue);
  }, [urlValue]);

  const onChange = useCallback(
    (next: string) => {
      setText(next);
      pending.current.push(next);
      write(next);
    },
    [write],
  );

  return [text, onChange];
}
